import { Controller } from "@hotwired/stimulus"

/**
 * Auto-save controller for forms that save automatically on field changes.
 *
 * Usage:
 *   <div data-controller="auto-save"
 *        data-auto-save-url-value="/path/to/update"
 *        data-auto-save-method-value="PATCH">
 *
 *     <div data-auto-save-target="status"></div>
 *
 *     <input data-action="input->auto-save#fieldChanged" name="model[field]">
 *     <select data-action="change->auto-save#fieldChanged" name="model[field]">
 *   </div>
 *
 * States: idle → pending → submitting → saved → idle
 *                                    → error
 */
export default class extends Controller {
  static targets = ["status"]

  static values = {
    url: String,
    method: { type: String, default: "PATCH" },
    debounce: { type: Number, default: 500 },
    savedDuration: { type: Number, default: 2500 }
  }

  connect() {
    this.state = "idle"
    this.debounceTimer = null
    this.savedTimer = null
    this.lastError = null
    this.errorExpanded = false
    this.render()
  }

  disconnect() {
    this.clearTimers()
  }

  clearTimers() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    if (this.savedTimer) {
      clearTimeout(this.savedTimer)
      this.savedTimer = null
    }
  }

  fieldChanged() {
    this.clearTimers()
    this.setState("pending")

    this.debounceTimer = setTimeout(() => {
      this.save()
    }, this.debounceValue)
  }

  async save() {
    this.setState("submitting")

    try {
      const formData = this.collectFormData()

      const response = await fetch(this.urlValue, {
        method: this.methodValue,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        this.setState("saved")
        this.savedTimer = setTimeout(() => {
          this.setState("idle")
        }, this.savedDurationValue)
      } else {
        this.lastError = data.message || data.errors?.join(", ") || "An error occurred"
        this.setState("error")
      }
    } catch (error) {
      console.error("Auto-save error:", error)
      this.lastError = "Network error. Please try again."
      this.setState("error")
    }
  }

  setState(newState) {
    this.state = newState
    if (newState !== "error") {
      this.errorExpanded = false
    }
    this.render()
  }

  toggleError(event) {
    event.preventDefault()
    this.errorExpanded = !this.errorExpanded
    this.render()
  }

  render() {
    if (!this.hasStatusTarget) return

    const target = this.statusTarget

    switch (this.state) {
      case "idle":
        target.innerHTML = ""
        break

      case "pending":
      case "submitting":
        target.innerHTML = `
          <span class="text-gray-400 flex items-center gap-1">
            <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Saving...
          </span>
        `
        break

      case "saved":
        target.innerHTML = `
          <span class="text-gray-400 flex items-center gap-1">
            <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            Saved
          </span>
        `
        break

      case "error":
        const errorHtml = this.errorExpanded
          ? `
            <div class="text-red-600">
              <button type="button" class="flex items-center gap-1 hover:text-red-700" data-action="click->auto-save#toggleError">
                <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Error
                <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                </svg>
              </button>
              <div class="mt-1 text-xs text-red-500 max-w-xs">${this.escapeHtml(this.lastError)}</div>
            </div>
          `
          : `
            <button type="button" class="text-red-600 hover:text-red-700 flex items-center gap-1" data-action="click->auto-save#toggleError">
              <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Error
              <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          `
        target.innerHTML = errorHtml
        break
    }
  }

  collectFormData() {
    const inputs = this.element.querySelectorAll("input, select, textarea")
    const data = {}

    inputs.forEach(input => {
      if (!input.name) return

      // Parse field name like "rtq[virgin_group]" or "renewal_rate_sheet[General.Credibility]" into nested object
      const match = input.name.match(/^(\w+)\[([^\]]+)\]$/)
      if (match) {
        const [, modelName, fieldName] = match
        if (!data[modelName]) data[modelName] = {}

        if (input.type === "checkbox") {
          data[modelName][fieldName] = input.checked
        } else {
          data[modelName][fieldName] = input.value
        }
      } else {
        // Simple field name
        if (input.type === "checkbox") {
          data[input.name] = input.checked
        } else {
          data[input.name] = input.value
        }
      }
    })

    return data
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
